import {
  AngleDownIcon,
  AngleUpIcon,
  Body,
  Box,
  Button,
  Card,
  Dialog,
  FormRow,
  FormSection,
  HeadingG4P,
  InfoIcon,
  InsuranceIcon,
  Space,
  Stack,
  Upselling,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import {
  LocaleKey,
  useFormatMessage,
  useFormatMoneyAmountValue,
  useFormatPercentageLike,
  useFormatRichMessage2,
} from "../../intl";
import { useCommand } from "../../useAPI";
import * as apis from "./api";
import { constNull, pipe } from "fp-ts/function";
import { option, taskEither } from "fp-ts";
import * as classes from "./OfferChooseCard.css";
import { useState } from "react";
import { GenericLoanOffer, GenericLoanResponseOutput } from "../api";
import { ReadOnlyFieldToValue } from "../../Common/ReadOnlyField/ReadOnlyFieldToValue";
import { spaceUnit } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";

type Props = {
  selected?: boolean;
  onComplete: (selectedOffer: GenericLoanResponseOutput) => void;
  offer: GenericLoanOffer;
  expanded: boolean;
};

export function OfferChooseCard(props: Props) {
  const formatMessage = useFormatMessage();
  const formatRichMessage = useFormatRichMessage2();
  const formatMoneyAmount = useFormatMoneyAmountValue();
  const formatPercentage = useFormatPercentageLike();
  const [moreInfo, setMoreInfo] = useState<boolean>(false);
  const [showUpsellConfirmation, setShowUpsellConfirmation] = useState<boolean>(
    false
  );
  const [upsellConfirmed, setUpsellConfirmed] = useState<boolean>(false);
  const saveSelectedOffer = useCommand(apis.saveSelectedOffer);
  const saveConsentUpsell = useCommand(apis.saveConsentUpsell);

  const offerType = pipe(
    props.offer.offerType,
    option.getOrElse(() => "CUSTOMER_OFFER")
  );

  const onOfferConfirmed = pipe(
    saveSelectedOffer(props.offer),
    taskEither.chain(selectedOffer =>
      taskEither.fromIO(() => props.onComplete(selectedOffer))
    )
  );

  const onOfferChoose =
    (offerType === "HIGHER_AMOUNT" || offerType === "EXTENDED_DURATION") &&
    !upsellConfirmed
      ? taskEither.fromIO(() => setShowUpsellConfirmation(true))
      : onOfferConfirmed;

  const isMobileLayout = useIsMobileLayout();
  const [isCollapsed, setIsCollapsed] = useState(!props.expanded);
  const isContentVisible = !isMobileLayout || (isMobileLayout && !isCollapsed);

  const cardTitle = () =>
    pipe(
      props.offer.offerType,
      option.fold(
        () =>
          formatMessage("StandardLoan.OfferApproved.ChooseCard.yourOfferTitle"),
        offerType => {
          switch (offerType) {
            case "EXTENDED_DURATION":
              return formatMessage(
                "StandardLoan.OfferApproved.ChooseCard.extendedDurationOffer"
              );
            case "HIGHER_AMOUNT":
              return formatMessage(
                "StandardLoan.OfferApproved.ChooseCard.higherAmountOffer"
              );
            case "COUNTER_OFFER":
              return formatMessage(
                "StandardLoan.CounterOffer.counterOfferTitle"
              );
            case "CounterOfferSocial1":
              return formatMessage(
                "StandardLoan.CounterOffer.CounterOfferSocial1.counterOfferTitle"
              );
            case "CounterOfferSocial2":
              return formatMessage(
                "StandardLoan.CounterOffer.CounterOfferSocial2.counterOfferTitle"
              );
            case "CUSTOMER_OFFER":
              return formatMessage(
                "StandardLoan.OfferApproved.ChooseCard.yourOfferTitle"
              );
          }
        }
      )
    );

  return (
    <Card>
      <Stack
        column
        grow
        shrink
        units={8}
        className={
          isMobileLayout ? classes.cardContentMobile : classes.cardContent
        }
      >
        <Stack
          units={2}
          vAlignContent={isMobileLayout ? "center" : "top"}
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={
            isMobileLayout
              ? { minWidth: spaceUnit * 65 }
              : { minWidth: spaceUnit * 65, height: spaceUnit * 12 }
          }
        >
          <HeadingG4P size="small" weight="bold">
            {cardTitle()}
          </HeadingG4P>
          {isMobileLayout &&
            (isCollapsed ? (
              <AngleDownIcon size="small" className={classes.angleIcon} />
            ) : (
              <AngleUpIcon size="small" className={classes.angleIcon} />
            ))}
        </Stack>
        <Stack column grow shrink units={4}>
          <Stack column units={4}>
            <FormSection>
              {pipe(
                props.offer.newMoneyAmount,
                option.fold(constNull, newMoneyAmount => (
                  <FormRow type="full">
                    <ReadOnlyFieldToValue
                      size="big"
                      label={formatMessage(
                        "StandardLoan.OfferApproved.ChooseCard.refinancingAmount"
                      )}
                      value={formatMoneyAmount({
                        amount: newMoneyAmount,
                        currency: props.offer.currencyCode,
                      })}
                      valueSize={"big"}
                      labelWeight={"bold"}
                    />
                  </FormRow>
                ))
              )}
              <FormRow type="full">
                <ReadOnlyFieldToValue
                  size="medium"
                  label={formatMessage(
                    "StandardLoan.OfferApproved.ChooseCard.loanAmount"
                  )}
                  value={formatMoneyAmount({
                    amount: props.offer.borrowedAmount,
                    currency: props.offer.currencyCode,
                  })}
                  labelWeight={"bold"}
                />
              </FormRow>
              {isContentVisible && (
                <FormRow type="full">
                  <ReadOnlyFieldToValue
                    size="medium"
                    label={formatMessage(
                      "StandardLoan.OfferApproved.ChooseCard.monthlyInstallment"
                    )}
                    value={formatMoneyAmount({
                      amount: props.offer.installmentAmount,
                      currency: props.offer.currencyCode,
                    })}
                    labelWeight={"bold"}
                  />
                </FormRow>
              )}
              {isContentVisible && (
                <FormRow type="full">
                  <ReadOnlyFieldToValue
                    size="medium"
                    label={formatMessage(
                      "StandardLoan.OfferApproved.ChooseCard.durationOfInstallments"
                    )}
                    value={formatMessage(
                      "StandardLoan.OfferApproved.ChooseCard.durationOfInstallmentsValue",
                      {
                        duration: props.offer.tenor,
                      }
                    )}
                    labelWeight={"bold"}
                  />
                </FormRow>
              )}
              {isContentVisible && (
                <FormRow type="full">
                  <ReadOnlyFieldToValue
                    size="medium"
                    label={formatMessage(
                      "StandardLoan.OfferApproved.ChooseCard.interestRate"
                    )}
                    value={formatMessage(
                      "StandardLoan.OfferApproved.ChooseCard.interestRateValue",
                      {
                        rate: formatPercentage(props.offer.interestRateDecimal),
                      }
                    )}
                    labelWeight={"bold"}
                  />
                </FormRow>
              )}
              {isContentVisible &&
                pipe(
                  props.offer.cpimonthlyAmount,
                  option.fold(constNull, cpimonthlyAmount => (
                    <FormRow type="full">
                      <ReadOnlyFieldToValue
                        size="medium"
                        label={formatMessage(
                          "StandardLoan.OfferApproved.ChooseCard.cpiMonthlyAmount"
                        )}
                        value={formatMoneyAmount({
                          amount: cpimonthlyAmount,
                          currency: props.offer.currencyCode,
                        })}
                        labelWeight={"bold"}
                      />
                    </FormRow>
                  ))
                )}
              {isContentVisible &&
                pipe(
                  props.offer.apr,
                  option.fold(constNull, apr => (
                    <FormRow type="full">
                      <ReadOnlyFieldToValue
                        size="medium"
                        label={formatMessage(
                          "StandardLoan.OfferApproved.ChooseCard.apr"
                        )}
                        value={formatMessage("Percentage.pa", {
                          percentage: formatMessage("Percentage", {
                            value: apr,
                          }),
                        })}
                        labelWeight={"bold"}
                      />
                    </FormRow>
                  ))
                )}
              {isContentVisible &&
                pipe(
                  props.offer.totalDueAmount,
                  option.fold(constNull, totalDueAmount => (
                    <FormRow type="full">
                      <ReadOnlyFieldToValue
                        size="medium"
                        label={formatMessage(
                          "StandardLoan.OfferApproved.ChooseCard.totalDueAmount"
                        )}
                        value={formatMoneyAmount({
                          amount: totalDueAmount,
                          currency: props.offer.currencyCode,
                        })}
                        labelWeight={"bold"}
                      />
                    </FormRow>
                  ))
                )}
            </FormSection>
            <></>
          </Stack>
          <Space fluid />
          {pipe(
            props.offer.cpiSelectedName,
            option.fold(constNull, cpiName =>
              pipe(
                props.offer.cpimonthlyAmount,
                option.fold(constNull, () => (
                  <Upselling
                    included
                    content={formatMessage(
                      "StandardLoan.OfferApproved.ChooseCard.insurance",
                      {
                        insurance: formatMessage(cpiName),
                      }
                    )}
                    icon={InsuranceIcon}
                  />
                ))
              )
            )
          )}
          {isContentVisible && (
            <>
              <Button
                type="button"
                label={formatMessage(
                  `StandardLoan.OfferApproved.ChooseCard.chooseOption.MoreInfo.${offerType}` as LocaleKey
                )}
                variant="text"
                size="small"
                action={() => {
                  setMoreInfo(true);
                }}
              />
              {moreInfo && (
                <Dialog
                  variant="center"
                  size="medium"
                  title={formatMessage(
                    `StandardLoan.OfferApproved.ChooseCard.chooseOption.MoreInfo.title.${offerType}` as LocaleKey
                  )}
                  onDismiss={option.some(() => setMoreInfo(false))}
                  actions={[]}
                  icon={InfoIcon}
                >
                  <Box
                    column
                    shrink
                    style={{ marginLeft: "6px", marginRight: "6px" }}
                  >
                    <Body size={"medium"} weight={"regular"}>
                      {formatRichMessage(
                        `StandardLoan.OfferApproved.ChooseCard.chooseOption.MoreInfo.body.${offerType}` as LocaleKey
                      )}
                    </Body>
                  </Box>
                </Dialog>
              )}
              {showUpsellConfirmation && (
                <Dialog
                  variant="center"
                  size="medium"
                  title={formatMessage(
                    `StandardLoan.OfferApproved.ChooseCard.chooseOption.UpsellConfirmation.title`
                  )}
                  onDismiss={option.some(() =>
                    setShowUpsellConfirmation(false)
                  )}
                  isSubmitting={upsellConfirmed && showUpsellConfirmation}
                  actions={[
                    {
                      variant: "primary",
                      action: pipe(
                        saveConsentUpsell(),
                        taskEither.chain(() =>
                          taskEither.fromIO(() => setUpsellConfirmed(true))
                        ),
                        taskEither.chain(() => onOfferConfirmed),
                        taskEither.chain(() =>
                          taskEither.fromIO(() =>
                            setShowUpsellConfirmation(false)
                          )
                        )
                      ),
                      label: formatMessage(
                        `StandardLoan.OfferApproved.ChooseCard.chooseOption.UpsellConfirmation.button`
                      ),
                    },
                  ]}
                  icon={InfoIcon}
                >
                  <Box
                    column
                    shrink
                    style={{ marginLeft: "6px", marginRight: "6px" }}
                  >
                    <Body size={"medium"} weight={"regular"}>
                      {formatRichMessage(
                        `StandardLoan.OfferApproved.ChooseCard.chooseOption.UpsellConfirmation.body`
                      )}
                    </Body>
                  </Box>
                </Dialog>
              )}
              <Space units={2} />
              <Button
                label={formatMessage(
                  "StandardLoan.OfferApproved.ChooseCard.chooseOption"
                )}
                variant="primary"
                size="default"
                action={onOfferChoose}
              />
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}
